/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { IModuleStateManager, IModuleStateProps, withModuleState } from '@msdyn365-commerce-modules/checkout-utilities';
import { IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';
import classname from 'classnames';
import get from 'lodash/get';
import { reaction } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { ICheckoutSectionContainerData } from './checkout-section-container.data';
import { ICheckoutSectionContainerProps } from './checkout-section-container.props.autogenerated';
import LoadingComponent from './components/loading';
import ReCAPTCHA from 'react-google-recaptcha';

export interface ICheckoutSectionContainerModuleProps extends ICheckoutSectionContainerProps<ICheckoutSectionContainerData>, IModuleStateProps {
    enableControl: boolean;
}

export enum PaymentType {
    Adyen = 'checkout-payment-instrument',
    PayByAccount = 'citta-checkout-customer-account-payment'
}
export interface ICheckoutSectionContainerItem {
    id: string;
    loading?: React.ReactNode;
    itemProps: INodeProps;
    item: React.ReactNode;
    accordion:React.ReactNode
}

export interface ICheckoutSectionContainerState {
    activeAccordion: string;
}

export interface ICheckoutSectionContainerState {
    reCaptchaChecked: boolean;
    rCCError: boolean;
    activeAccordion: string;
}

export interface ICheckoutSectionContainerViewProps extends ICheckoutSectionContainerProps<{}> {
    className: string;
    checkoutPlainContainerProps: IModuleProps;
    items: ICheckoutSectionContainerItem[];
    // accordion: React.ReactNode;
    reCaptcha: React.ReactNode;
}

/**
 *
 * CheckoutPlainContainer component
 * @extends {React.Component<ICheckoutSectionContainerModuleProps<ICheckoutSectionContainerData>>}
 */
@observer
export class CheckoutPlainContainer extends React.Component<ICheckoutSectionContainerModuleProps, ICheckoutSectionContainerState> {
    
    public constructor(props:ICheckoutSectionContainerModuleProps) {
        super(props);
        this.state = {
            activeAccordion:PaymentType.PayByAccount,
            reCaptchaChecked: false,
            rCCError: false,
        };

    }
    // public state: ICheckoutSectionContainerState = {
    //     reCaptchaChecked: false,
    //     rCCError: false,
    //     activeAccordion:PaymentType.PayByAccount
    // }
    private _recaptchaRef: React.RefObject<ReCAPTCHA> = React.createRef();
    private recaptchaErrorMessage: string | undefined;
    public componentDidMount(): void {
        const items = this.getItems();
        const childIds = items.map((item: React.ReactNode) => get(item, 'props.id'));
        this.props.moduleState.init({
            onEdit: this.onEdit,
            onCancel: this.onCancel,
            onSubmit: this.onSubmit,
            childIds,
        }
    );

        reaction(
            () => this.props.moduleState.isReady,
            () => {
                this.onContainerReady();
            }
        );

        reaction(
            () => this.props.moduleState.shouldSubmitContainer,
            () => {
                this.onSubmit();
            }
        );
    }

    public componentDidUpdate(): void {
        if (!this.props.moduleState.hasExternalSubmitGroup) {
            return;
        }
        /**
         * Review all modules to updating status if one of the modules has error
         */
        const { childIds, hasError, getModule } = this.props.moduleState;
        const hasReadyItems = childIds.some((childId: string) => {
            const state = getModule(childId);
            return !!state && state.isReady;
        });
        if (hasReadyItems && hasError) {
            this.onEdit();
        }
    }
    public render(): JSX.Element | null {
        const {
            enableControl,
            moduleState,
            config: { width, paymentAccordion, creditCardIcon }
        } = this.props;
        const items = this.getItems();
        if (!items.length) {
            this.props.context.telemetry.error('Checkout section content is empty, module wont render');
            return null;
        }

        const moduleClassName = classname(
            'ms-checkout-section-container',
            {
                'has-initialized': moduleState.hasInitialized,
                'is-ready': moduleState.isReady,
                'is-updating': !moduleState.isReady,
                container: width === 'container',
                'col-12 fullwidth': width === 'fluid'
            },
            this.props.config.className
        );

        const viewProps: ICheckoutSectionContainerViewProps = {
            ...this.props,

            className: moduleClassName,
            reCaptcha: this._renderReCaptcha(),
            checkoutPlainContainerProps: {
                moduleProps: this.props,
                className: moduleClassName
            },
            items: items.map((item: React.ReactNode) => {
                const childId = get(item, 'props.id');
                const childTypeName = get(item, "props.typeName");
                const state = moduleState.getModule(childId);
                const customerInfo = get(this.props, 'data.customerInformation.result');
                const platform = get(this.props, 'context.request.app.platform');
                const isAllowOnAccountPayment = customerInfo && customerInfo.AllowOnAccountPayment &&  platform &&
                (platform.enableCustomerAccountPayment === 'all' ||
                    (platform.enableCustomerAccountPayment === 'b2b' && customerInfo.IsB2b === true) ||
                    (platform.enableCustomerAccountPayment === 'b2c' && customerInfo.IsB2b === false))
                // Modules has not completed initializing or it's disable 

                // let isPayment = childTypeName ===PaymentType.PayByAccount || childTypeName ===PaymentType.Adyen
                const hidden = state.hasModuleState && (!state.hasInitialized || state.isDisabled || state.isSkipped)||(isAllowOnAccountPayment && paymentAccordion? this.state.activeAccordion !== childTypeName: '');
                
                return {
                    id: childId,
                    loading: state.hasModuleState && !state.hasInitialized && <LoadingComponent />,
                    itemProps: {
                        'data-type-name': state.typeName,
                        className: classname('ms-checkout-section-container__item', {
                            hidden: hidden,
                            initialized: state.hasInitialized,
                            disabled: state.isDisabled,
                            skipped: state.isSkipped,
                            ready: state.isReady,
                            updating: state.isUpdating,
                            pending: state.isPending
                        })
                    },
                    accordion:isAllowOnAccountPayment && paymentAccordion? this._renderAccordionItem(item, creditCardIcon): null,
                    item: React.cloneElement(item as React.ReactElement, { enableControl })
                };
            })
        };
        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private _renderReCaptcha(): React.ReactNode {
        if(this.props.config.reCaptchaOption){
            return (
                <div>
                    <div className=''>
                        {this.state.rCCError ? this._renderRecaptchaAlert('recaptcha-error') : ''}
                        <ReCAPTCHA
                            ref={this._recaptchaRef}
                            sitekey={this.props.config.reCaptchaSiteKey!}
                            size="invisible"
                            onChange={() => this._submit()}
                        />
                    </div>
                </div>
            );
        }
        return null;
    }

    private _renderRecaptchaAlert(className: string): JSX.Element {

        if (this.props.config.reCaptchaOption) {
            if (!this.state.reCaptchaChecked) {
                this.recaptchaErrorMessage = this.props.resources.recaptchaErrorMessage;
            }
        }

        const rCCError = this.state.rCCError;
        return (
            <>
                {rCCError && (
                    <span className={`${className}__alert`} role='alert' aria-live='assertive'>
                        {
                            this.recaptchaErrorMessage &&
                            <>
                                <span className={`${className}__alert-icon`} aria-hidden='true' />
                                {this.recaptchaErrorMessage}
                            </>
                        }
                    </span>
                )}
            </>
        );
    }

    private _submit() {
        const recaptchaValue = this._recaptchaRef.current?.getValue();
        this.clearError();
        if(!recaptchaValue){
            this.setState({
                rCCError: true
            });
            this._recaptchaRef.current?.reset();
        } else {
            this.getActiveChildModuleStates().forEach(state => {
            !state.isReady && state.onSubmit && state.onSubmit();
            });
            this._recaptchaRef.current?.reset();
        }
    }

    private getItems = (): React.ReactNode[] => {
        const { slots } = this.props;
        return (slots && slots.primary && slots.primary.length && slots.primary) || [];
    };

    private _renderAccordionItem(item:any, creditCardIcon:any): React.ReactNode{
        const childTypeName = get(item, 'props.typeName');
        let slotName:string = '';
        switch(childTypeName) {
            case PaymentType.PayByAccount:
            slotName = 'Charge to Account';
            break;
            case PaymentType.Adyen:
            slotName = 'Credit Card';
            break;
            default:null
        }
        return (
            <div className={`accordion-item ${this.props.moduleState.isReady && childTypeName!==this.state.activeAccordion? 'border-hidden' : ''}`}  style={{"display": this.props.moduleState.isReady && childTypeName!==this.state.activeAccordion? "none" : "block"}}>
                        <input type="radio" className="accordion-item__Radio" name={slotName} checked={childTypeName===this.state.activeAccordion} onChange={() => this.updateAccordion(childTypeName)} />
                        <div className="accordion-item__Header">{slotName}</div>
                        {childTypeName === PaymentType.Adyen?  <div className='credit-card-icon-wrap'><img className='credit-card-icon' src={creditCardIcon} alt="" /></div> : null}
                    </div>
        )
    }

    public updateAccordion(newValue: string): void {
        if(this.state.activeAccordion===PaymentType.PayByAccount){
            this.props.moduleState.getModuleByTypeName(this.state.activeAccordion).onSkip();
        }
        this.setState({activeAccordion: newValue});
        this.props.moduleState.getModuleByTypeName(newValue).onUpdating();
        if(newValue===PaymentType.PayByAccount){
            this.props.moduleState.getModuleByTypeName(PaymentType.Adyen).onDisable()
        } else {
            this.props.moduleState.getModuleByTypeName(PaymentType.PayByAccount).onDisable()
        }
    }

    private getActiveChildModuleStates = (): IModuleStateManager[] => {
        const { childIds, getModule } = this.props.moduleState;
        return childIds
            .map(getModule)
            .filter((state): state is IModuleStateManager => !!state && !state.isDisabled && state.hasModuleState);
    };

    private clearError = (): void => {
        // Clear error before action, otherwise it will rise conflict;
        this.getActiveChildModuleStates().forEach(state => {
            state.hasError && state.setHasError(false);
        });
    };

    private onSubmit = (): void => {
        if(this.props.config.reCaptchaOption){
            this._recaptchaRef.current?.execute()
        } else {
            this.clearError();
            this.getActiveChildModuleStates().forEach(state => {
                !state.isReady && state.onSubmit && state.onSubmit();
            });
        }
    };

    private onCancel = (): void => {
        this.clearError();
        this.getActiveChildModuleStates().forEach(state => {
            !state.isReady && state.onCancel && state.onCancel();
        });
    };

    private onEdit = (): void => {
        const {
            config: {paymentAccordion}, data
        } = this.props;
        const isAllowOnAccountPayment = data.customerInformation.result?.AllowOnAccountPayment
        this.getActiveChildModuleStates().forEach(state => {
            (state.isReady || state.isSkipped) && state.onEdit && state.onEdit();
        });
        isAllowOnAccountPayment && paymentAccordion && this.props.moduleState.getModuleByTypeName(PaymentType.Adyen).onDisable()
    };

    private onContainerReady = (): void => {
        this.getActiveChildModuleStates().forEach(state => {
            state.isReady && state.onContainerReady && state.onContainerReady();
        });
    };
}

export default withModuleState(CheckoutPlainContainer);
